import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Heart, Loader2, ExternalLink } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { loadPlayOffer, purchasePlay, verifyPlayPurchase, PLAY_MANAGE_URL } from "../lib/playBilling";

const TRIAL_DAYS = 7;

export default function PlayPaywall({ me, onChanged }) {
  const { user } = useAuth();
  const [offer, setOffer] = useState(null);
  const [hasTrial, setHasTrial] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    loadPlayOffer().then((r) => { setOffer(r.offer); setHasTrial(r.hasTrial); }).catch((e) => setError(e.message));
  }, []);

  const subscribe = async () => {
    setBusy(true);
    try {
      const tx = await purchasePlay(user.id, offer);
      try {
        await verifyPlayPurchase(tx);
        toast.success("You're Sorted! Welcome aboard, legend");
      } catch {
        toast("Purchase received — Millie's confirming it with Google Play, hang tight");
      }
      onChanged?.();
    } catch (e) {
      if (!/cancel/i.test(e?.message || "")) toast.error(e?.message || "Google Play couldn't complete that");
    } finally { setBusy(false); }
  };

  const price = offer?.priceString || "A$9.95";
  const active = me?.provider === "google_play" && me?.tier > 0;

  return (
    <div className="mx-6 mt-5 bg-white rounded-3xl border-2 border-sorted-rose p-5 shadow-soft" data-testid="play-paywall">
      <div className="flex items-center gap-3">
        <span className="w-11 h-11 rounded-2xl bg-[#FCE1E8] text-sorted-rose flex items-center justify-center"><Heart size={20} strokeWidth={2.5} /></span>
        <div className="flex-1">
          <p className="font-heading font-semibold text-sorted-ink leading-tight">Sorted <span className="ml-1 text-[10px] font-bold bg-sorted-rose text-white px-2 py-0.5 rounded-full">Most loved</span></p>
          <p className="text-[11px] text-sorted-slate font-semibold">Unlimited plans, Millie chat, Kitchen scan, Family sharing</p>
        </div>
        <div className="text-right">
          <p className="font-heading font-semibold text-sorted-ink text-lg leading-none" data-testid="play-price">{price}</p>
          <p className="text-[10px] font-bold text-sorted-slate">/month</p>
        </div>
      </div>

      {active ? (
        <a href={PLAY_MANAGE_URL} target="_blank" rel="noreferrer" data-testid="play-manage"
          className="mt-4 w-full py-3 rounded-full border-2 border-[#FDECEF] text-sm font-bold text-sorted-ink flex items-center justify-center gap-1.5">
          <Check size={15} strokeWidth={3} /> Your plan · Manage in Google Play <ExternalLink size={13} />
        </a>
      ) : (
        <button onClick={subscribe} disabled={!offer || busy} data-testid="play-subscribe"
          className="mt-4 w-full py-3 rounded-full text-sm font-bold bg-sorted-rose text-white flex items-center justify-center gap-1.5 disabled:opacity-60 active:scale-[0.98] transition-transform">
          {busy ? <Loader2 size={15} className="animate-spin" /> : null}
          {busy ? "Opening Google Play…" : hasTrial ? `Start ${TRIAL_DAYS}-day free trial` : `Subscribe · ${price}/month`}
        </button>
      )}
      {error && <p className="mt-2 text-[11px] font-bold text-sorted-rose" data-testid="play-error">{error}</p>}

      <p className="mt-3 text-[11px] text-sorted-slate font-semibold leading-relaxed" data-testid="play-terms">
        {hasTrial ? `Free for ${TRIAL_DAYS} days, then ${price} per month. ` : `${price} per month. `}
        Billed monthly through Google Play and renews automatically until cancelled. Cancel any time in
        {" "}<a href={PLAY_MANAGE_URL} target="_blank" rel="noreferrer" className="underline underline-offset-2">Google Play → Subscriptions</a>;
        {hasTrial ? " cancel before the trial ends and you won't be charged." : " access continues until the end of the billing period."}
      </p>
    </div>
  );
}
